import { Scenario } from '../../domain/types.js';

export const SCENARIOS_KEY = 'mortgage-simulator.scenarios.v1';
export const PREFS_KEY = 'mortgage-simulator.preferences.v1';

/** Version of the payload stored under SCENARIOS_KEY. Bump when the Scenario shape changes. */
export const SCHEMA_VERSION = 2;

export type Preferences = {
  locale: 'ru' | 'en';
  lastViewedId?: string;
  baselineId?: string;
  /** Scenarios the user added to the comparison, in insertion order. The store caps and filters them. */
  comparisonIds?: string[];
};

export type StoredScenarios = {
  schemaVersion: number;
  scenarios: Scenario[];
};

export type Migration = (payload: StoredScenarios) => StoredScenarios;

/**
 * Keyed by the schema version a migration upgrades FROM; every step must
 * return a payload with a higher schemaVersion. The current version keeps an
 * identity entry so the table doubles as the list of known versions.
 * Version 1 scenarios keep their original monthly calculation unchanged.
 */
export const migrations: Record<number, Migration> = {
  1: payload => ({ ...payload, schemaVersion: 2 }),
  2: payload => payload,
};

const LOCALES: readonly Preferences['locale'][] = ['ru', 'en'];
const defaultPreferences: Preferences = { locale: 'ru' };

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isNumber(value: unknown): value is number {
  return typeof value === 'number';
}

function isOptional<T>(value: unknown, check: (v: unknown) => v is T): value is T | undefined {
  return value === undefined || check(value);
}

function isString(value: unknown): value is string {
  return typeof value === 'string';
}

function isRatePeriodShape(value: unknown): boolean {
  return isRecord(value) && isNumber(value.startMonth) && isNumber(value.endMonth) && isNumber(value.rateApr);
}

function isRecurrenceShape(value: unknown): boolean {
  return isRecord(value) && isOptional(value.endMonth, isNumber) && isNumber(value.step);
}

function isAdaptiveShape(value: unknown): boolean {
  return (
    isRecord(value) &&
    isNumber(value.targetAmount) &&
    isNumber(value.minAmount) &&
    isOptional(value.endMonth, isNumber) &&
    isNumber(value.step)
  );
}

function isOverpaymentShape(value: unknown): boolean {
  return (
    isRecord(value) &&
    isNumber(value.month) &&
    isNumber(value.amount) &&
    isOptional(value.strategy, isString) &&
    (value.recurring === undefined || isRecurrenceShape(value.recurring)) &&
    (value.adaptive === undefined || isAdaptiveShape(value.adaptive))
  );
}

function isRefinanceShape(value: unknown): boolean {
  return (
    isRecord(value) &&
    isNumber(value.month) &&
    isNumber(value.newRateApr) &&
    isNumber(value.newTermMonths) &&
    isOptional(value.repaymentType, isString)
  );
}

function isBankCalendarShape(value: unknown): boolean {
  return isRecord(value) && isString(value.disbursementDate) && isNumber(value.paymentDay)
    && isOptional(value.recalculationMonths, (months: unknown): months is number[] => Array.isArray(months) && months.every(isNumber));
}

/** Structural check: every field the domain reads is present with the right primitive type. */
export function isScenarioShape(value: unknown): value is Scenario {
  return (
    isRecord(value) &&
    isString(value.id) &&
    isString(value.name) &&
    isNumber(value.principal) &&
    isNumber(value.termMonths) &&
    isNumber(value.rateApr) &&
    isString(value.repaymentType) &&
    isString(value.startDate) &&
    isString(value.overpaymentStrategy) &&
    isOptional(value.calculationMode, isString) &&
    (value.bankCalendar === undefined || isBankCalendarShape(value.bankCalendar)) &&
    Array.isArray(value.ratePeriods) && value.ratePeriods.every(isRatePeriodShape) &&
    Array.isArray(value.overpayments) && value.overpayments.every(isOverpaymentShape) &&
    Array.isArray(value.refinances) && value.refinances.every(isRefinanceShape)
  );
}


/**
 * Normalise a raw stored value into the current payload shape. Bare arrays
 * (written before the payload carried a version) are read as version 1.
 * Returns null for unknown shapes, unknown versions and payloads newer than
 * this build, so a downgrade never misreads data.
 */
export function migrateStoredScenarios(
  raw: unknown,
  table: Record<number, Migration> = migrations,
  targetVersion: number = SCHEMA_VERSION,
): StoredScenarios | null {
  let payload: StoredScenarios;
  if (Array.isArray(raw)) {
    payload = { schemaVersion: 1, scenarios: raw as Scenario[] };
  } else if (isRecord(raw) && Number.isInteger(raw.schemaVersion) && Array.isArray(raw.scenarios)) {
    payload = { schemaVersion: raw.schemaVersion as number, scenarios: raw.scenarios as Scenario[] };
  } else {
    return null;
  }

  while (payload.schemaVersion < targetVersion) {
    const step = table[payload.schemaVersion];
    if (!step) return null;
    const next = step(payload);
    if (!Number.isInteger(next.schemaVersion) || next.schemaVersion <= payload.schemaVersion) return null;
    payload = next;
  }
  return payload.schemaVersion === targetVersion ? payload : null;
}

function readStoredScenarios(storage: Storage): Scenario[] {
  try {
    const raw = storage.getItem(SCENARIOS_KEY);
    if (!raw) return [];
    const payload = migrateStoredScenarios(JSON.parse(raw));
    if (!payload) return [];
    // Keep everything that has the right shape. Business-rule validation is
    // the store's job (it reports rejected ids), so a scenario that a newer
    // rule rejects is not silently purged by the next read-modify-write.
    return payload.scenarios.filter(isScenarioShape);
  } catch {
    return [];
  }
}

function writeItem(storage: Storage, key: string, value: string): boolean {
  try {
    storage.setItem(key, value);
    return true;
  } catch {
    // QuotaExceededError, SecurityError or a read-only storage: the caller
    // keeps working with in-memory state.
    return false;
  }
}

function writeScenarios(storage: Storage, scenarios: Scenario[]): boolean {
  const payload: StoredScenarios = { schemaVersion: SCHEMA_VERSION, scenarios };
  return writeItem(storage, SCENARIOS_KEY, JSON.stringify(payload));
}

// Synchronous load to prevent race conditions during read-modify-write.
export function loadScenarios(): Scenario[] {
  const storage = getStorage();
  if (!storage) return [];
  return readStoredScenarios(storage);
}

/** Read-modify-write of one scenario. Returns false when nothing could be written. */
export function saveScenario(next: Scenario): boolean {
  const storage = getStorage();
  if (!storage) return false;

  const scenarios = readStoredScenarios(storage);
  const existingIndex = scenarios.findIndex(s => s.id === next.id);
  if (existingIndex >= 0) {
    scenarios[existingIndex] = next;
  } else {
    scenarios.push(next);
  }
  return writeScenarios(storage, scenarios);
}

export function deleteScenario(id: string): boolean {
  const storage = getStorage();
  if (!storage) return false;
  const scenarios = readStoredScenarios(storage);
  return writeScenarios(storage, scenarios.filter(s => s.id !== id));
}

export function loadPreferences(): Preferences {
  const storage = getStorage();
  if (!storage) return defaultPreferences;
  try {
    const raw = storage.getItem(PREFS_KEY);
    if (!raw) return defaultPreferences;
    const parsed: unknown = JSON.parse(raw);
    if (!isRecord(parsed)) return defaultPreferences;
    const prefs: Preferences = { ...defaultPreferences };
    if (isString(parsed.locale) && (LOCALES as readonly string[]).includes(parsed.locale)) {
      prefs.locale = parsed.locale as Preferences['locale'];
    }
    if (isString(parsed.lastViewedId)) prefs.lastViewedId = parsed.lastViewedId;
    if (isString(parsed.baselineId)) prefs.baselineId = parsed.baselineId;
    if (Array.isArray(parsed.comparisonIds)) prefs.comparisonIds = parsed.comparisonIds.filter(isString);
    return prefs;
  } catch {
    return defaultPreferences;
  }
}

export function savePreferences(prefs: Preferences): boolean {
  const storage = getStorage();
  if (!storage) return false;
  return writeItem(storage, PREFS_KEY, JSON.stringify(prefs));
}

